import { ACCEPTED_EXTENSIONS, ACCEPTED_TYPES, MAX_FILE_BYTES } from '@/constants/media';
import { formatBytes } from '@/utils/format';

export type FileValidationCode = 'empty' | 'too-large' | 'unsupported-type' | 'content-mismatch';

export interface FileValidationError {
  code: FileValidationCode;
  message: string;
}

export type FileValidationResult = { ok: true } | { ok: false; error: FileValidationError };

type Container = 'mp4' | 'webm' | 'ogg' | 'avi';

function ascii(bytes: Uint8Array, start: number, end: number): string {
  return String.fromCharCode(...bytes.subarray(start, end));
}

/** Sniff the container from leading magic bytes. Returns null when unknown. */
export function detectContainer(bytes: Uint8Array): Container | null {
  if (bytes.length >= 12) {
    if (ascii(bytes, 4, 8) === 'ftyp') return 'mp4'; // MP4 / MOV / M4V family
    if (ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 12) === 'AVI ') return 'avi';
  }
  if (bytes.length >= 4) {
    if (bytes[0] === 0x1a && bytes[1] === 0x45 && bytes[2] === 0xdf && bytes[3] === 0xa3) {
      return 'webm'; // EBML header — WebM / Matroska
    }
    if (ascii(bytes, 0, 4) === 'OggS') return 'ogg';
  }
  return null;
}

function hasAcceptedExtension(name: string): boolean {
  const lower = name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

export interface ValidateOptions {
  /** Injectable for tests. */
  maxBytes?: number;
}

/** Read the first bytes of a file, with a FileReader fallback for
 * environments where `Blob.arrayBuffer` is missing (legacy Safari, jsdom). */
async function readHeadBytes(file: File, length: number): Promise<Uint8Array> {
  const slice = file.slice(0, length);
  if (typeof slice.arrayBuffer === 'function') {
    return new Uint8Array(await slice.arrayBuffer());
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file.'));
    reader.readAsArrayBuffer(slice);
  });
}

/**
 * Defense-in-depth validation before any bytes reach the decoder:
 * size limits, MIME/extension allowlist, then a magic-byte sniff so a
 * renamed non-video (or non-allowlisted container) is rejected up front.
 */
export async function validateVideoFile(
  file: File,
  options: ValidateOptions = {},
): Promise<FileValidationResult> {
  const maxBytes = options.maxBytes ?? MAX_FILE_BYTES;

  if (file.size === 0) {
    return { ok: false, error: { code: 'empty', message: 'That file is empty.' } };
  }
  if (file.size > maxBytes) {
    return {
      ok: false,
      error: {
        code: 'too-large',
        message: `That file is ${formatBytes(file.size)} — the limit is ${formatBytes(maxBytes)}.`,
      },
    };
  }

  const typeAccepted = file.type !== '' && file.type in ACCEPTED_TYPES;
  if (!typeAccepted && !hasAcceptedExtension(file.name)) {
    return {
      ok: false,
      error: {
        code: 'unsupported-type',
        message: 'Use an MP4, WebM, MOV, MKV, OGV or AVI file.',
      },
    };
  }

  const head = await readHeadBytes(file, 16);
  if (detectContainer(head) === null) {
    return {
      ok: false,
      error: {
        code: 'content-mismatch',
        message: "This file's contents don't match a supported video format.",
      },
    };
  }

  return { ok: true };
}

/**
 * Probe duration and coded size with a detached, muted `<video>` element.
 * Acts as the final functional validation: if the browser can't parse the
 * metadata, we reject the file before offering any editing.
 */
export function loadVideoMetadata(
  url: string,
  timeoutMs = 15_000,
): Promise<{ duration: number; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;

    const timer = window.setTimeout(() => {
      fail(new Error('Timed out reading video metadata.'));
    }, timeoutMs);

    function cleanup() {
      window.clearTimeout(timer);
      video.removeAttribute('src');
      video.load();
    }
    function fail(err: Error) {
      cleanup();
      reject(err);
    }

    video.onloadedmetadata = () => {
      const { duration, videoWidth, videoHeight } = video;
      cleanup();
      if (!Number.isFinite(duration) || duration <= 0 || videoWidth === 0 || videoHeight === 0) {
        reject(new Error('This video reports no readable duration or size.'));
        return;
      }
      resolve({ duration, width: videoWidth, height: videoHeight });
    };
    video.onerror = () => fail(new Error('The browser could not decode this video.'));
    video.src = url;
  });
}
